import { FC, useState } from 'react';
import { css } from '@emotion/react';
import { CustomKnowledge } from '@weave/schema-gen-ts/dist/schemas/ai-settings/v1/ai_settings.pb';
import isEqual from 'lodash-es/isEqual';
import { Icon } from '@frontend/icons';
import { MultiLocationAccordionControl } from '@frontend/multi-location-accordion-control';
import { useAppScopeStore } from '@frontend/scope';
import { Accordion, ContentLoader } from '@frontend/design-system';
import { CustomKnowledgeForm } from './custom-knowledge-form';

type Props = {
  locationIds: string[];
  settings: Record<string, CustomKnowledge>;
  initialSettings: Record<string, CustomKnowledge>;
  locationErrors: Record<string, boolean>;
  isLoading?: boolean;
};

export const MultiLocationCustomKnowledge: FC<Props> = ({
  locationIds,
  settings,
  initialSettings,
  locationErrors,
  isLoading,
}) => {
  const [filteredLocationIds, setFilteredLocationIds] = useState<string[]>(locationIds);
  const { getLocationName } = useAppScopeStore();

  return (
    <MultiLocationAccordionControl>
      <MultiLocationAccordionControl.Header
        filteredLocationIds={filteredLocationIds}
        setFilteredLocationIds={setFilteredLocationIds}
      />

      <MultiLocationAccordionControl.Accordion
        startingValue={filteredLocationIds[0]}
        createAccordionBody={(locationId: string) => {
          const isDirty = !isEqual(settings[locationId], initialSettings[locationId]);
          const hasError = !!locationErrors[locationId];

          return (
            <Accordion.Item value={locationId}>
              <Accordion.Header css={styles.accordionHeader}>
                <Accordion.Header.Location title={getLocationName(locationId)} className='location-header' />
                {hasError && <Icon name='error-filled' color='error' />}
                {!hasError && isDirty && <Icon name='alert-filled' color='warn' />}
              </Accordion.Header>
              <Accordion.Body
                css={css`
                  padding: var(--fabric-space-padding-lg);
                  position: relative;
                `}
              >
                <ContentLoader show={isLoading} />
                <CustomKnowledgeForm locationId={locationId} />
              </Accordion.Body>
            </Accordion.Item>
          );
        }}
        filteredLocationIds={filteredLocationIds}
      />
    </MultiLocationAccordionControl>
  );
};

const styles = {
  accordionHeader: css`
    .header-container {
      padding: var(--fabric-space-gap-md) 0px var(--fabric-space-gap-md) var(--fabric-space-gap-md);
      display: grid;
      grid-template-columns: 1fr auto;
      align-items: center;
      gap: var(--fabric-space-gap-sm);

      .location-header {
        justify-self: start;
      }
    }
  `,
};
